
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  section: 'Resources',
  title: 'Vibe coding tips',
  description: 'Get the best results when using AI tools with Plaid'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title={metadata.title} subtitle={metadata.description} mdxType="Header" />
    <p>{`When using AI coding tools like Cursor or Claude Code with Plaid, following a few best practices can help you get more accurate results.`}</p>
    <h4 {...{
      "id": "ensure-you-have-the-latest-plaid-client-library-and-sdk-installed"
    }}>{`Ensure you have the latest Plaid client library and SDK installed`}</h4>
    <p>{`If using an AI tool to set up your environment, manually double-check to make sure that the most recent `}<a parentName="p" {...{
        "href": "/docs/api/libraries/"
      }}>{`Plaid client library`}</a>{` and, if applicable, the most recent `}<a parentName="p" {...{
        "href": "/docs/api/libraries/#link-client-sdks"
      }}>{`frontend SDK`}</a>{` is installed. AI tools love to install outdated versions of these libraries! `}</p>
    <p>{`When an outdated Plaid library or SDK is installed, not only will you not be able to use the latest Plaid products and features, but the AI tool will tend to use the locally installed documentation, which is itself outdated, and will not discover new products and features.`}</p>
    <h4 {...{
      "id": "explicitly-include-the-specific-docs-pages-you-want-to-use-in-your-context"
    }}>{`Explicitly include the specific docs pages you want to use in your context`}</h4>
    <p>{`The Plaid docs site is too large to fit in any commercially available LLM's context window. You may need to prompt the tool with the specific URLs you want to read docs from, rather than simply having it crawl `}<a parentName="p" {...{
        "href": "https://plaid.com/docs"
      }}>{`https://plaid.com/docs`}</a>{`. For best results, use the `}<a parentName="p" {...{
        "href": "/docs/resources/#llm-friendly-documentation"
      }}>{`LLM-friendly documentation`}</a>{`. Alternatively, install the `}<a parentName="p" {...{
        "href": "https://github.com/plaid/ai-coding-toolkit/tree/main/sandbox"
      }}>{`Plaid AI coding toolkit`}</a>{` -- its `}<inlineCode parentName="p">{`search_documentation`}</inlineCode>{` tool lets your AI coding assistant find and pull in the right docs pages on its own.`}</p>
    <h4 {...{
      "id": "use-the-integration-overviews-launch-checklist-andor-vibe-coding-guides-to-create-a-high-level-task-list"
    }}>{`Use the integration overviews, Launch Checklist, and/or vibe coding guides to create a high-level task list`}</h4>
    <p>{`Most products' documentation includes a high-level integration overview list of steps. (`}<a parentName="p" {...{
        "href": "/docs/check/#standard-integration-flow"
      }}>{`Example for Plaid Check Consumer Report`}</a>{`, `}<a parentName="p" {...{
        "href": "/docs/transactions/#integration-overview"
      }}>{`example for Transactions`}</a>{`). Once you have Production access, a more comprehensive and personalized list of steps can be found in the `}<a parentName="p" {...{
        "href": "https://dashboard.plaid.com/developers/launch-center"
      }}>{`Launch Center`}</a>{`. For Signal Transaction Scores, Transfer, and Transactions, Plaid also has `}<a parentName="p" {...{
        "href": "https://github.com/plaid/ai-coding-toolkit/tree/main/rules"
      }}>{`vibe-coding guides`}</a>{`, designed to be used as input to an AI assistant to help it implement these integrations correctly. `}</p>
    <p>{`Giving these steps to an AI tool can help make sure it implements the right steps. (Of course, none of this is a substitute for making sure you understand the high level steps yourself.) `}</p>
    <h4 {...{
      "id": "use-sample-apps-requests-and-responses-as-context"
    }}>{`Use sample apps, requests, and responses as context`}</h4>
    <p>{`Providing your app with existing, known-working sample code from the `}<a parentName="p" {...{
        "href": "https://github.com/plaid"
      }}>{`Plaid public GitHub`}</a>{` or sample requests and responses from the `}<a parentName="p" {...{
        "href": "/docs/api/"
      }}>{`API Reference`}</a>{` or `}<a parentName="p" {...{
        "href": "/docs/link"
      }}>{`Link docs`}</a>{` to use as context helps LLMs to better discover the correct interfaces for Plaid API calls. Providing a sample response as context can be particularly helpful when working with endpoints that have complex and deeply nested responses, like those used by Income or Consumer Report. (Providing the OpenAPI specification as context does not work as well as providing a JSON response or code sample.)`}</p>
    <h4 {...{
      "id": "dont-forget-dashboard-setup-steps"
    }}>{`Don't forget Dashboard setup steps`}</h4>
    <p>{`Many Plaid products require configuration steps in the Dashboard that are not currently exposed to MCP servers and can't be configured by AI. For products like Identity Verification, Monitor, Layer, Signal Transaction Scores, Balance, and Protect, you will need to use the Dashboard to set up rules and/or templates in order for your integration to work.`}</p>
    <h4 {...{
      "id": "ensure-you-are-using-the-correct-test-data"
    }}>{`Ensure you are using the correct test data`}</h4>
    <p>{`Several Plaid products, especially Identity Verification, require specific test inputs and configurations in Sandbox to receive a successful result, a fact which AI coding tools often miss. Make sure to read the testing documentation for the product you're building and that the test data sent by your app is consistent with the test data expected by Plaid.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "ensure-you-have-the-latest-plaid-client-library-and-sdk-installed",
        level: 4,
        title: "Ensure you have the latest Plaid client library and SDK installed",
      },
{
        id: "explicitly-include-the-specific-docs-pages-you-want-to-use-in-your-context",
        level: 4,
        title: "Explicitly include the specific docs pages you want to use in your context",
      },
{
        id: "use-the-integration-overviews-launch-checklist-andor-vibe-coding-guides-to-create-a-high-level-task-list",
        level: 4,
        title: "Use the integration overviews, Launch Checklist, and/or vibe coding guides to create a high-level task list",
      },
{
        id: "use-sample-apps-requests-and-responses-as-context",
        level: 4,
        title: "Use sample apps, requests, and responses as context",
      },
{
        id: "dont-forget-dashboard-setup-steps",
        level: 4,
        title: "Don't forget Dashboard setup steps",
      },
{
        id: "ensure-you-are-using-the-correct-test-data",
        level: 4,
        title: "Ensure you are using the correct test data",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
